import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { MissingDocumentError } from '@/lib/document-download-errors';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import { useMode } from '@/providers/ModeProvider';

/** Extract the plain `filename` value from a Content-Disposition header. */
function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  // Try quoted filename first: filename="..."
  const quoted = /(?:^|;)\s*filename\s*=\s*"([^"]+)"/.exec(header);
  if (quoted) return quoted[1];
  // Try unquoted filename: filename=value (stop at ; or whitespace)
  const unquoted = /(?:^|;)\s*filename\s*=\s*([^;\s"]+)/.exec(header);
  return unquoted?.[1] ?? null;
}

export interface DocumentAttachment {
  base64Content: string;
  fileName: string;
  contentType: string;
  /** Set when the document was downloaded for a specific transaction. */
  transactionId?: number;
}

interface TransactionDocumentDownloadState {
  isDownloading: boolean;
  downloadTransactionDocument: (transactionId: number) => Promise<void>;
  downloadDocumentAsBase64: (transactionId: number) => Promise<DocumentAttachment>;
}

/**
 * Hook to view a transaction document via the backend endpoint.
 * Fetches the document as a blob and opens it in a new browser tab.
 * In demo mode the button is a no-op.
 */
export function useTransactionDocumentDownload(): TransactionDocumentDownloadState {
  const { apiBaseUrl, getHeaders } = useApiConfig();
  const { isDemo } = useMode();
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadTransactionDocument = useCallback(
    async (transactionId: number) => {
      if (isDemo) {
        // In demo mode, do nothing - avoid opening example URLs
        return;
      }

      setIsDownloading(true);
      try {
        const headers = await getHeaders();
        // Remove Content-Type for blob request - the browser will set it
        const { 'Content-Type': _contentType, ...headersWithoutContentType } = headers as Record<string, string>;
        void _contentType;

        const response = await fetch(`${apiBaseUrl}/api/v1/transactions/${transactionId}/document/download`, {
          method: 'GET',
          headers: headersWithoutContentType,
        });

        if (!response.ok) {
          if (response.status === 404) {
            toast.error('No document available for this transaction');
            return;
          }
          if (response.status === 400) {
            toast.error('Document download is not supported for this transaction type');
            return;
          }
          toast.error('Failed to download document');
          return;
        }

        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        const filename = parseFilenameFromContentDisposition(contentDisposition) ?? `transaction_${transactionId}.pdf`;
        const contentType = response.headers.get('Content-Type') ?? 'application/pdf';

        const blobUrl = URL.createObjectURL(new Blob([blob], { type: contentType }));

        // Open in a new tab for viewing
        const newTab = window.open(blobUrl, '_blank');
        if (!newTab) {
          // Fallback: trigger download if popup blocked
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        // Clean up the blob URL after a delay
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } catch (error) {
        console.error('Document download failed:', error);
        toast.error('Failed to download document');
      } finally {
        setIsDownloading(false);
      }
    },
    [apiBaseUrl, getHeaders, isDemo],
  );

  /**
   * Download a transaction document and return it as a base64-encoded
   * attachment suitable for embedding in a SendIt MFE email payload.
   */
  const downloadDocumentAsBase64 = useCallback(
    async (transactionId: number): Promise<DocumentAttachment> => {
      const headers = await getHeaders();
      const { 'Content-Type': _ct, ...headersWithoutCt } = headers as Record<string, string>;
      void _ct;

      const response = await fetch(`${apiBaseUrl}/api/v1/transactions/${transactionId}/document/download`, {
        method: 'GET',
        headers: headersWithoutCt,
      });

      if (!response.ok) {
        if (response.status === 404 || response.status === 400) {
          throw new MissingDocumentError(`No document available for transaction ${transactionId}`);
        }
        throw new Error(`Failed to download document for transaction ${transactionId} (HTTP ${response.status})`);
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      const fileName = parseFilenameFromContentDisposition(contentDisposition) ?? `invoice_${transactionId}.pdf`;
      const contentType = response.headers.get('Content-Type') ?? 'application/pdf';

      const base64Content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          // Strip the data:...;base64, prefix
          resolve(result.split(',')[1] || '');
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      return { base64Content, fileName, contentType, transactionId };
    },
    [apiBaseUrl, getHeaders],
  );

  return { isDownloading, downloadTransactionDocument, downloadDocumentAsBase64 };
}
